import { useId, useState } from 'react';
import { Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';

interface TrackingIconImageProps {
  imageUrl?: string | null;
  name: string;
  size?: number;
  backgroundColor?: string;
}

interface ImagePreviewProps {
  imageUrl: string;
  name: string;
  onClose: () => void;
}

const ImagePreview = ({ imageUrl, name, onClose }: ImagePreviewProps) => {
  const titleId = useId();
  const [originalSize, setOriginalSize] = useState(false);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} sx={{ pr: 7, overflowWrap: 'anywhere' }}>
        {name}
        <IconButton
          onClick={onClose}
          aria-label='Cerrar vista previa'
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, height: '60vh', position: 'relative', bgcolor: '#F9FAFB' }}
      >
        {loadFailed ? (
          <Box role='alert' sx={{ p: 4, textAlign: 'center' }}>
            No se pudo cargar la imagen. Cerrá la vista previa y recargá el
            listado para volver a intentarlo.
          </Box>
        ) : (
          <>
            {!dimensions && (
              <Skeleton
                variant='rectangular'
                animation='wave'
                width='100%'
                height='100%'
                role='status'
                aria-label='Cargando imagen'
                sx={{
                  position: 'absolute',
                  inset: 0,
                }}
              />
            )}
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: originalSize ? 'max-content' : '100%',
                height: originalSize ? 'auto' : '100%',
                minWidth: '100%',
                minHeight: '100%',
              }}
            >
              <Box
                component='img'
                src={imageUrl}
                alt={name}
                referrerPolicy='no-referrer'
                onLoad={(event) =>
                  setDimensions({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
                onError={() => setLoadFailed(true)}
                sx={{
                  display: 'block',
                  visibility: dimensions ? 'visible' : 'hidden',
                  width: originalSize ? dimensions?.width : '100%',
                  height: originalSize ? dimensions?.height : '100%',
                  maxWidth: 'none',
                  maxHeight: originalSize ? 'none' : '60vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        {dimensions && !loadFailed && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mr: 'auto' }}
          >
            {dimensions.width} × {dimensions.height} px
          </Typography>
        )}
        <Button
          onClick={() => setOriginalSize(false)}
          variant={originalSize ? 'outlined' : 'contained'}
          aria-pressed={!originalSize}
          disabled={!dimensions || loadFailed}
        >
          Ajustar a ventana
        </Button>
        <Button
          onClick={() => setOriginalSize(true)}
          variant={originalSize ? 'contained' : 'outlined'}
          aria-pressed={originalSize}
          disabled={!dimensions || loadFailed}
        >
          Tamaño original (100%)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TrackingIconImage = ({
  imageUrl,
  name,
  size = 56,
  backgroundColor = '#F9FAFB',
}: TrackingIconImageProps) => {
  const [open, setOpen] = useState(false);
  const [imageState, setImageState] = useState<{
    url: string;
    failed: boolean;
  } | null>(null);
  const isLoading = !!imageUrl && imageState?.url !== imageUrl;
  const loadFailed = imageState?.url === imageUrl && imageState?.failed;
  const canPreview = !!imageUrl && !isLoading && !loadFailed;

  return (
    <>
      <Tooltip title={canPreview ? 'Ampliar imagen' : ''} arrow describeChild>
        <ButtonBase
          onClick={() => setOpen(true)}
          disabled={!canPreview}
          aria-busy={isLoading}
          aria-label={
            imageUrl ? `Ver imagen de ${name}` : `Sin imagen de ${name}`
          }
          aria-haspopup='dialog'
          sx={{
            position: 'relative',
            width: size,
            maxWidth: '100%',
            height: size,
            borderRadius: 1,
            bgcolor: backgroundColor,
            color: '#6B7280',
            border: '1px solid #E5E7EB',
            cursor: 'zoom-in',
            '&:hover': { borderColor: 'primary.main' },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 3,
            },
          }}
        >
          {imageUrl && !loadFailed ? (
            <Box
              key={imageUrl}
              component='img'
              src={imageUrl}
              alt={name}
              onLoad={() => setImageState({ url: imageUrl, failed: false })}
              onError={() => setImageState({ url: imageUrl, failed: true })}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: 'inherit',
                visibility: isLoading ? 'hidden' : 'visible',
              }}
            />
          ) : (
            <span className='text-xs text-center leading-tight'>
              {loadFailed ? 'No se pudo cargar' : 'Sin imagen'}
            </span>
          )}
          {isLoading && (
            <Skeleton
              variant='rounded'
              animation='wave'
              width='100%'
              height='100%'
              role='status'
              aria-label={`Cargando imagen de ${name}`}
              sx={{ position: 'absolute', inset: 0 }}
            />
          )}
          {canPreview && (
            <Box
              component='span'
              aria-hidden='true'
              sx={{
                position: 'absolute',
                right: 2,
                bottom: 2,
                display: 'grid',
                placeItems: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'primary.main',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
              }}
            >
              <ZoomInIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
        </ButtonBase>
      </Tooltip>
      {open && imageUrl && (
        <ImagePreview
          key={imageUrl}
          imageUrl={imageUrl}
          name={name}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default TrackingIconImage;
